import { Node, Edge, useViewport, useStore } from "@xyflow/react";
import styles from "./DevTools.module.css";
import {
  Alert,
  Grid,
  Button,
  Spacer,
  Surface,
  Text,
  TextInput,
  ToggleSwitch,
  Tag,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useCallback, useState, useEffect, Suspense } from "react";
import {
  nodesAtom,
  edgesAtom,
  canvasAtom,
  Canvas,
  ZapsMap,
  shouldShowStepIdsAtom,
  isViewOnlyModeAtom,
  togglesAtomResettable,
  zapsAtom_SUSPENDS,
  generateZslSetter,
  generateZslErrorAtom,
  canvasNameAtom,
  zslAtom,
  makeImagesPublicSetter,
  ZSL,
  ZapMetadata,
  zslCreatorBaseUrlAtom,
  showJotaiDevtoolsAtom,
  showDebugAtom,
  isZapNode,
  isDefaultNode,
  isTextNode,
} from "@zapier/canvas-state";
import { zslToCanvasErrorAtom, zslToCanvasPostSetter } from "@zapier/streaming";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";

import { CopyToClipboardTextarea } from "../../components/CopyToClipboardTextarea";
import { composeCanvasForTemplate } from "../../utils/composeCanvasForTemplate";
import { toKebabCase } from "../../../../service/src/app/utils/stringUtils";

type Data = Node[] | Edge[] | Partial<Canvas> | ZapsMap | ZSL;
const serialize = (data: Data) => JSON.stringify(data, null, 2);

const isNotEmpty = (obj: object) => Object.keys(obj).length > 0;

const selector = ({ userSelectionActive, userSelectionRect }: any) => ({
  userSelectionActive,
  userSelectionRect,
});

function getZapCount(zaps: Record<string, ZapMetadata>): number {
  return Object.keys(zaps).length;
}

const SuspendedZapsSection = () => {
  const zaps = useAtomValue(zapsAtom_SUSPENDS);
  const rawZapsData = serialize(zaps);
  const hasZaps = isNotEmpty(zaps);

  if (!hasZaps) {
    return null;
  }

  const zapCount = getZapCount(zaps);

  return (
    <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
      <div className={styles.devtoolsInner}>
        <Text type="Body3">Zaps ({zapCount})</Text>
        <CopyToClipboardTextarea value={rawZapsData} />
      </div>
    </Surface>
  );
};

const ZapsTextArea = () => {
  return (
    <Suspense fallback={null}>
      <SuspendedZapsSection />
    </Suspense>
  );
};

const DevTools = () => {
  const [zslCreatorBaseUrl, setZslCreatorBaseUrl] = useAtom(
    zslCreatorBaseUrlAtom,
  );
  const [showJotaiDevtools, setShowJotaiDevtools] = useAtom(
    showJotaiDevtoolsAtom,
  );
  const [showAiToolkitDebug, setShowAiToolkitDebug] = useAtom(showDebugAtom);

  const {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    edges: canvasEdges,
    nodes: canvasNodes,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ...rest
  } = useAtomValue(canvasAtom);
  const zsl = useAtomValue(zslAtom);
  const store = useStore(selector);

  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);
  const [shouldShowStepIds, setShouldShowStepIds] = useAtom(
    shouldShowStepIdsAtom,
  );

  const [toggles, setToggles] = useAtom(togglesAtomResettable);
  const resetAllFeatureFlags = useResetAtom(togglesAtomResettable);

  const togglesEntries = Object.entries(toggles);
  const [isViewOnlyMode, setIsViewOnlyMode] = useAtom(isViewOnlyModeAtom);
  const canvasName = useAtomValue(canvasNameAtom);

  const handleResetAllFeatureFlags = useCallback(() => {
    resetAllFeatureFlags();
  }, [resetAllFeatureFlags]);

  const createFeatureFlagRows = () => {
    return togglesEntries.map(([key, value]) => {
      const isBooleanFlagWithConfig =
        typeof value === "object" && value !== null && "isOn" in value;
      const isStringFlagWithConfig =
        typeof value === "object" && value !== null && "treatment" in value;

      // If it's a string (not boolean), show as a tag without toggle
      if (typeof value === "string") {
        return (
          <li key={key} className={styles.featureFlagRowWrapper}>
            <div className={styles.featureFlagRow}>
              <Text type="SmallPrint3">{key}</Text>
              <div className={styles.toggleWrapper}>
                <Tag variant="GrayWarm3">{value}</Tag>
              </div>
            </div>
          </li>
        );
      }

      // If it's a flag with treatment (not isOn), show treatment as tag without toggle
      if (isStringFlagWithConfig) {
        return (
          <li key={key} className={styles.featureFlagRowWrapper}>
            <div className={styles.featureFlagRow}>
              <Text type="SmallPrint3">{key}</Text>
              <div className={styles.toggleWrapper}>
                <Tag variant="GrayWarm3">{(value as any).treatment}</Tag>
              </div>
            </div>
          </li>
        );
      }

      // For boolean flags and flags with isOn, show toggle
      return (
        <li key={key} className={styles.featureFlagRowWrapper}>
          <div className={styles.featureFlagRow}>
            <Text type="SmallPrint3">{key}</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={
                  isBooleanFlagWithConfig ? (value as any).isOn : Boolean(value)
                }
                onChange={() => {
                  if (isBooleanFlagWithConfig) {
                    setToggles((prevToggles) => ({
                      ...prevToggles,
                      [key]: {
                        ...(value as object),
                        isOn: !(value as any).isOn,
                      },
                    }));
                  } else {
                    // For simple boolean flags, just toggle the boolean value
                    setToggles((prevToggles) => ({
                      ...prevToggles,
                      [key]: !value,
                    }));
                  }
                }}
              />
            </div>
          </div>
        </li>
      );
    });
  };

  const viewport = useViewport();

  const rawCanvasData = serialize(rest);
  const rawNodesData = serialize(nodes);
  const rawEdgesData = serialize(edges);
  const rawZslData = zsl ? serialize(zsl) : undefined;
  const rawReactFlowStore = serialize(store);

  const hasNodes = nodes.length > 0;
  const hasEdges = edges.length > 0;

  const convertZslToCanvas = useSetAtom(zslToCanvasPostSetter);
  const [localZsl, setLocalZsl] = useState("");
  const [zslToCanvasError, setZslToCanvasError] = useAtom(zslToCanvasErrorAtom);

  const generateZsl = useSetAtom(generateZslSetter);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [generateZslError, setGenerateZslError] = useAtom(generateZslErrorAtom);
  const [template, setTemplate] = useState(null);

  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleChangeZslToCanvas = (event: any) => {
    setLocalZsl(event.target.value);
  };

  const handleSubmitZslToCanvas = async () => {
    try {
      //TODO: Ideally, we would do the saving inside of the Atom, which is not possible currently
      await convertZslToCanvas(localZsl);
    } catch (error) {
      console.error("Error converting ZSL to Canvas:", error);
    }
  };

  const makeImagesPublic = useSetAtom(makeImagesPublicSetter);

  const handleSubmitExportCanvas = async () => {
    const assetIds = {
      zapIds: [] as string[],
      tableIds: [] as string[],
      chatbotIds: [] as string[],
      interfaceIds: [] as string[],
      agentIds: [] as string[],
    };

    nodes.forEach((node) => {
      if (
        isZapNode(node) &&
        node.data.zapId &&
        !assetIds.zapIds.includes(node.data.zapId)
      ) {
        assetIds.zapIds.push(node.data.zapId);
      } else if (
        isDefaultNode(node) &&
        node.data.tableId &&
        !assetIds.tableIds.includes(node.data.tableId)
      ) {
        assetIds.tableIds.push(node.data.tableId);
      } else if (
        isDefaultNode(node) &&
        node.data.chatbotId &&
        !assetIds.chatbotIds.includes(node.data.chatbotId)
      ) {
        assetIds.chatbotIds.push(node.data.chatbotId);
      } else if (
        isDefaultNode(node) &&
        node.data.projectId &&
        !assetIds.interfaceIds.includes(node.data.projectId)
      ) {
        assetIds.interfaceIds.push(node.data.projectId);
      } else if (
        isDefaultNode(node) &&
        node.data.agentId &&
        !assetIds.agentIds.includes(node.data.agentId)
      ) {
        assetIds.agentIds.push(node.data.agentId);
      }
    });

    // Collect all image IDs from nodes that aren't already public
    const imageIds: string[] = [];
    nodes.forEach((node) => {
      const extractImageIds = (content: any) => {
        if (
          content?.attrs?.src?.includes("/api/canvas/v1/canvases") &&
          !content.attrs.src.includes("/api/canvas/v1/canvases/public") // Skip if already public
        ) {
          const imageId = content.attrs.src.split("/").pop();
          if (imageId) imageIds.push(imageId);
        }
        if (content.content) {
          content.content.forEach((item: any) => extractImageIds(item));
        }
      };

      if (isDefaultNode(node) && node.data.notesJson?.content) {
        node.data.notesJson.content.forEach(extractImageIds);
      }
      if (isTextNode(node) && node.data.label?.content) {
        node.data.label.content.forEach(extractImageIds);
      }
    });

    try {
      setIsGeneratingTemplate(true);

      // Only make images public if there are images to process
      let imageIdMap: Record<string, string> = {};
      if (imageIds.length > 0) {
        imageIdMap = await makeImagesPublic(imageIds);
      }

      // Create new nodes with public image URLs
      const nodesWithPublicImages = nodes.map((node) => {
        const newNode = JSON.parse(JSON.stringify(node)); // Deep clone the node to avoid mutations
        const updateContent = (content: any) => {
          if (content?.attrs?.src?.includes("/api/canvas/v1/canvases")) {
            const imageId = content.attrs.src.split("/").pop();
            if (imageId && imageIdMap[imageId]) {
              content.attrs.src = `/api/canvas/v1/canvases/public/images/${imageIdMap[imageId]}`;
            }
          }
          if (content.content) {
            content.content.forEach(updateContent);
          }
        };

        if (newNode.data.notesJson?.content) {
          newNode.data.notesJson.content.forEach(updateContent);
        }
        if (newNode.data.label?.content) {
          newNode.data.label.content.forEach(updateContent);
        }
        return newNode;
      });

      const data = await generateZsl(assetIds);
      const template = data.template;
      template.redirectTo = "_CANVAS_1";
      template.name = canvasName || "New Template";
      template.canvases = [
        composeCanvasForTemplate(
          canvasName,
          nodesWithPublicImages,
          edges,
          data.resourceMap,
        ),
      ];
      setTemplate(template);
      setIsGeneratingTemplate(false);
    } catch (error) {
      console.error("Error exporting Canvas:", error);
      setIsGeneratingTemplate(false);
    }
  };

  const handleSubmitDownloadTemplate = () => {
    if (!template) return;

    const jsonContent = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `${toKebabCase(canvasName) || "untitled"}-template.json`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTemplate(null);
  };

  useEffect(() => {
    const contentDiv = document.querySelector(
      'section[class*="_drawer_"] *[class*="_content_"]',
    );

    const checkIfAtBottom = () => {
      if (!contentDiv) return;

      const bottomBuffer = 200; // Pixels from the bottom considered "at the bottom"

      const isBottom =
        contentDiv.scrollHeight - contentDiv.clientHeight <=
        contentDiv.scrollTop + bottomBuffer;

      setIsAtBottom(isBottom);
    };

    contentDiv?.addEventListener("scroll", checkIfAtBottom);

    // Clean up the event listener when the component unmounts
    return () => contentDiv?.removeEventListener("scroll", checkIfAtBottom);
  }, [setIsAtBottom]);

  const handleScrollToBottom = useCallback(() => {
    const contentDiv = document.querySelector(
      'section[class*="_drawer_"] *[class*="_content_"]',
    ) as HTMLElement;

    if (contentDiv) {
      contentDiv.style.scrollBehavior = "smooth";
      contentDiv.scrollTop = contentDiv.scrollHeight;
    }
  }, []);

  return (
    <div className={styles.devtoolsSectionWrapper}>
      {!isAtBottom && (
        <div className={styles.scrollDownButtonWrapper}>
          <Button
            size="medium"
            onPress={handleScrollToBottom}
            iconPosition="start"
          >
            <Icon name="arrowBigDown" />
            Scroll to bottom
          </Button>
        </div>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">Show step IDs</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={shouldShowStepIds}
                onChange={() => setShouldShowStepIds((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      </Surface>
      {process.env.NODE_ENV !== "production" && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <div className={styles.flexRow}>
              <Text type="Body3">Show Jotai Devtools</Text>
              <div className={styles.toggleWrapper}>
                <ToggleSwitch
                  checked={showJotaiDevtools}
                  onChange={() => setShowJotaiDevtools((prev) => !prev)}
                />
              </div>
            </div>
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">AI Toolkit Debug</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={showAiToolkitDebug}
                onChange={() => setShowAiToolkitDebug((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">View-Only Mode</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={isViewOnlyMode}
                onChange={() => {
                  setIsViewOnlyMode((prev) => !prev);
                }}
              />
            </div>
          </div>
        </div>
      </Surface>
      {hasNodes && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Nodes ({nodes.length})</Text>
            <CopyToClipboardTextarea value={rawNodesData} />
          </div>
        </Surface>
      )}
      {hasEdges && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Edges ({edges.length})</Text>
            <CopyToClipboardTextarea value={rawEdgesData} />
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Canvas</Text>
          <CopyToClipboardTextarea value={rawCanvasData} />
        </div>
      </Surface>
      <ZapsTextArea />
      {rawZslData && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">ZSL</Text>
            <CopyToClipboardTextarea value={rawZslData} />
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.featureFlagsHeader}>
            <Text type="Body3">Feature Flags</Text>
            <Icon name="actionFlag" size={24} />
          </div>
          <div className={styles.devToolsContentWrapper}>
            <ul className={styles.featureFlagsWrapper}>
              {createFeatureFlagRows()}
            </ul>
            <div className={styles.buttonWrapperAlignRight}>
              <Button size="compact" onPress={handleResetAllFeatureFlags}>
                Reset all
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">React Flow Store</Text>
          <CopyToClipboardTextarea value={rawReactFlowStore} rows={4} />
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Viewport</Text>
          <div className={styles.viewportWrapper}>
            <Grid columns={3}>
              <div className={styles.viewportCell}>
                <strong>x:</strong> {viewport.x.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>y:</strong> {viewport.y.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>zoom:</strong> {viewport.zoom.toFixed(1)}
              </div>
            </Grid>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Simulate Error</Text>
          <Text type="SmallPrint1" tag="div">
            Manually throw an error, so you can trace it in Datadog.
          </Text>
          <div className={styles.devToolsContentWrapperFlex}>
            <Button
              variant="danger"
              size="small"
              onPress={() => {
                throw new Error("Simulated error");
              }}
            >
              Throw Error
            </Button>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Convert ZSL to Canvas</Text>
          {zslToCanvasError && (
            <>
              <Alert
                title={zslToCanvasError}
                onDismiss={() => setZslToCanvasError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <TextInput
            isMultiline={true}
            rows={20}
            onChange={handleChangeZslToCanvas}
            value={localZsl}
            isErrored={!!zslToCanvasError}
          />
          <div className={styles.devToolsContentWrapper}>
            <div className={styles.buttonWrapperAlignRight}>
              <Button size="small" onPress={handleSubmitZslToCanvas}>
                Render Canvas
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Export Canvas to Template</Text>
          {generateZslError && (
            <>
              <Alert
                title={generateZslError}
                onDismiss={() => setGenerateZslError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <div className={styles.devToolsContentWrapperFlex}>
            {template ? (
              <Button size="small" onPress={handleSubmitDownloadTemplate}>
                Download Template
              </Button>
            ) : (
              <div className={styles.exportCanvasButton}>
                <Button
                  size="small"
                  onPress={handleSubmitExportCanvas}
                  isPending={isGeneratingTemplate}
                >
                  Export Canvas
                </Button>
              </div>
            )}
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Set ZSL Creator Base URL</Text>
          <TextInput
            value={zslCreatorBaseUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setZslCreatorBaseUrl(e.target.value)
            }
          />
        </div>
      </Surface>
    </div>
  );
};

export default DevTools;
